export const wikiTopicPages = {
  lana: {
    title: "Lana",
    contents: [
      ["overview", "Overview"],
      ["project", "The Project"],
      ["keyboard", "Keyboard Environment"],
      ["naming", "Naming and Reference"],
      ["limits", "Limits and Transition"],
      ["trajectory", "Place in the Trajectory"],
      ["references", "References"],
    ],
    lead:
      "Lana was the chimpanzee at the center of the original Language Analog Research Project at Yerkes. Her work with a computer-controlled Yerkish keyboard made ape symbol use visible, repeatable, and experimentally tractable.",
    sections: [
      {
        id: "project",
        title: "The Project",
        body:
          "Language work at Yerkes began in 1970 with Lana as the pilot animal. The immediate aim was methodological: build a system precise enough to study ape language without relying on face-to-face cueing, ambiguous gesture, or unrecorded exchanges. Lana's setting joined a keyboard, a computer, a display line, and response devices so each symbolic act could be recorded and evaluated.",
        refs: ["r1"],
        links: [
          { label: "Yerkes Primate Center", href: "/research/yerkes" },
          { label: "Yerkish", href: "/research/yerkish" },
        ],
      },
      {
        id: "keyboard",
        title: "Keyboard Environment",
        body:
          "Lana's keyboard used lexigrams as word-like units and displayed selected symbols in sequence. This mattered because the researchers were not just counting responses; they were creating a controlled symbolic environment in which word order, grammaticality, and machine-mediated consequences could be examined.",
        refs: ["r1", "r2"],
        links: [
          { label: "Lexigrams", href: "/research/lexigrams" },
          { label: "Ernst von Glasersfeld", href: "/research/ernst-von-glasersfeld" },
        ],
      },
      {
        id: "naming",
        title: "Naming and Reference",
        body:
          "Lana's early achievements included stock sentences for obtaining foods, drinks, or environmental events. The harder issue was whether symbols could detach from immediate reward and function as names. The 1978 status paper treats this transition from requesting to naming as a central problem for the entire research program.",
        refs: ["r1", "r4"],
        links: [{ label: "Lexigrams", href: "/research/lexigrams" }],
      },
      {
        id: "limits",
        title: "Limits and Transition",
        body:
          "The researchers did not treat Lana's work as a complete answer. They explicitly noted that with only one animal and an evolving technology, the project could document accomplishments but could not settle all questions. That is why the program expanded in 1976 to Sherman, Austin, Ericka, and Kenton.",
        refs: ["r1", "r3"],
        links: [{ label: "Sherman and Austin", href: "/research/sherman-and-austin" }],
      },
      {
        id: "trajectory",
        title: "Place in the Trajectory",
        body:
          "Lana is the starting point for the trajectory because she forced the key methodological questions into view: how to record symbolic acts, how to distinguish requesting from naming, how to minimize cueing, and how to ask whether a sequence of symbols can become more than trained performance.",
        refs: ["r1", "r2", "r4"],
      },
    ],
    references: ["r1", "r2", "r3", "r4"],
  },
  "sherman-and-austin": {
    title: "Sherman and Austin",
    contents: [
      ["overview", "Overview"],
      ["social", "Symbols Become Social"],
      ["method", "Food-Message Tasks"],
      ["reference", "Reference"],
      ["bridge", "Bridge to Bonobo Work"],
      ["references", "References"],
    ],
    lead:
      "Sherman and Austin were chimpanzees whose lexigram studies moved the research from individual symbol use into communication between apes.",
    sections: [
      {
        id: "social",
        title: "Symbols Become Social",
        body:
          "Sherman and Austin made the keyboard a social medium. Their studies asked whether one chimpanzee could use a lexigram to affect what another chimpanzee knew and did. This was a different question from whether an ape could request something from a human or operate a machine.",
        refs: ["r3"],
        links: [{ label: "Lexigrams", href: "/research/lexigrams" }],
      },
      {
        id: "method",
        title: "Food-Message Tasks",
        body:
          "In the food-container experiments, one chimpanzee knew what food was hidden and the other did not. The uninformed chimpanzee's choice depended on the informed chimpanzee's lexigram message. The design made symbolic communication testable because the recipient needed the message to solve the task.",
        refs: ["r3"],
      },
      {
        id: "reference",
        title: "Reference",
        body:
          "Their work sharpened the question of reference by asking whether a symbol could stand for something meaningful to another individual. The 1980 reference paper follows directly from this problem: a symbol must do more than produce a private reward if it is to count as linguistic evidence.",
        refs: ["r3", "r4"],
      },
      {
        id: "bridge",
        title: "Bridge to Bonobo Work",
        body:
          "Sherman and Austin bridge Lana and Kanzi. Lana established the controlled symbolic environment; Sherman and Austin made that environment intersubjective; the bonobo work later moved the question into development, comprehension, observation, and culture.",
        refs: ["r1", "r3", "r5", "r6"],
        links: [
          { label: "Lana", href: "/research/lana" },
          { label: "Kanzi", href: "/research/kanzi" },
        ],
      },
    ],
    references: ["r1", "r3", "r4", "r5", "r6"],
  },
  yerkish: {
    title: "Yerkish",
    contents: [
      ["overview", "Overview"],
      ["purpose", "Purpose"],
      ["design", "Design"],
      ["grammar", "Grammar"],
      ["afterlife", "Afterlife"],
      ["references", "References"],
    ],
    lead:
      "Yerkish was the artificial language designed for the original LANA Project. It made ape communication research operational by combining a lexigram keyboard, a vocabulary, and a grammar that could be parsed by computer.",
    sections: [
      {
        id: "purpose",
        title: "Purpose",
        body:
          "Yerkish was built because natural language was too ambiguous for the kind of experimental control the project required. A formal language allowed the researchers to ask whether an ape could produce valid symbol sequences, make requests in different ways, and work inside a rule-governed symbolic system.",
        refs: ["r1", "r2"],
      },
      {
        id: "design",
        title: "Design",
        body:
          "The system used lexigrams on keys, a display of selected symbols, computer parsing, and response devices. It was designed for computer-mediated communication, not as a simplified English. The result was a laboratory language that could be recorded, checked, and studied.",
        refs: ["r1", "r2"],
        links: [{ label: "Lexigrams", href: "/research/lexigrams" }],
      },
      {
        id: "grammar",
        title: "Grammar",
        body:
          "The grammar associated with Yerkish was correlational: it regulated which lexigram sequences were acceptable expressions. The Yerkish paper emphasizes von Glasersfeld's role in creating the lexigrams and designing the grammar that controlled their combination.",
        refs: ["r1", "r2"],
        links: [{ label: "Ernst von Glasersfeld", href: "/research/ernst-von-glasersfeld" }],
      },
      {
        id: "afterlife",
        title: "Afterlife",
        body:
          "Later keyboard systems did not always preserve Lana's full Yerkish grammar, but the methodological idea endured: give apes a stable symbolic medium and study what they can do with it under conditions that preserve records and reduce cueing.",
        refs: ["r1", "r5"],
      },
    ],
    references: ["r1", "r2", "r5"],
  },
  lexigrams: {
    title: "Lexigrams",
    contents: [
      ["overview", "Overview"],
      ["design", "Symbol Design"],
      ["keyboard", "Keyboard Use"],
      ["communication", "Communication"],
      ["continuity", "Continuity"],
      ["references", "References"],
    ],
    lead:
      "Lexigrams are abstract visual symbols used as word-like units in ape-language research. They are central to the Lana, Sherman and Austin, and Kanzi research trajectories.",
    sections: [
      {
        id: "design",
        title: "Symbol Design",
        body:
          "Lexigrams were deliberately abstract. The 1978 status paper describes them as distinctive geometric figures functioning as words, built from a small set of stimulus elements. The Yerkish paper emphasizes their non-representational design so they would not be mistaken for pictures.",
        refs: ["r1", "r2"],
      },
      {
        id: "keyboard",
        title: "Keyboard Use",
        body:
          "In the keyboard systems, lexigram selections could be displayed, recorded, parsed, and linked to requests, naming tasks, or machine responses. This made symbol use durable enough to analyze after the event, unlike many face-to-face gestures.",
        refs: ["r1", "r2"],
      },
      {
        id: "communication",
        title: "Communication",
        body:
          "With Sherman and Austin, lexigrams became a medium for interanimal messages. With Kanzi and other bonobos, lexigrams became part of a broader environment that included spoken English, gesture, observation, and everyday social routines.",
        refs: ["r3", "r5", "r6"],
        links: [
          { label: "Sherman and Austin", href: "/research/sherman-and-austin" },
          { label: "Kanzi", href: "/research/kanzi" },
        ],
      },
      {
        id: "continuity",
        title: "Continuity",
        body:
          "Lexigrams are the strongest visual through-line in the research trajectory. They connect Lana's early stock sentences, Sherman and Austin's food-message experiments, Kanzi's symbolic development, and later arguments about Pan communication and memory.",
        refs: ["r1", "r3", "r5", "r9"],
      },
    ],
    references: ["r1", "r2", "r3", "r5", "r6", "r9"],
  },
  yerkes: {
    title: "Yerkes Primate Center",
    contents: [
      ["overview", "Overview"],
      ["setting", "Research Setting"],
      ["system", "Technical System"],
      ["expansion", "Expansion"],
      ["references", "References"],
    ],
    lead:
      "Yerkes Primate Center was the institutional setting for the original Language Analog Research Project, where Lana's work with Yerkish began.",
    sections: [
      {
        id: "setting",
        title: "Research Setting",
        body:
          "The 1978 status paper describes the Yerkes language work as beginning in 1970 with Lana, a young female chimpanzee serving as the pilot animal. Yerkes provided the setting in which a keyboard, computer, projectors, displays, and vending or response systems could be integrated.",
        refs: ["r1"],
      },
      {
        id: "system",
        title: "Technical System",
        body:
          "The research environment mattered as much as the subject. By controlling symbol presentation and recording output, the system attempted to reduce cueing and preserve an empirical record of symbol sequences.",
        refs: ["r1"],
      },
      {
        id: "expansion",
        title: "Expansion",
        body:
          "After Lana, the project expanded to additional chimpanzees including Sherman and Austin so that questions of acquisition, use, reference, and interanimal communication could be tested more directly.",
        refs: ["r1", "r3"],
        links: [{ label: "Sherman and Austin", href: "/research/sherman-and-austin" }],
      },
    ],
    references: ["r1", "r3"],
  },
  kanzi: {
    title: "Kanzi",
    contents: [
      ["overview", "Overview"],
      ["development", "Developmental Context"],
      ["comprehension", "Language Comprehension"],
      ["observational", "Observational Learning"],
      ["tools", "Tools and Culture"],
      ["trajectory", "Place in the Trajectory"],
      ["references", "References"],
    ],
    lead:
      "Kanzi was a bonobo whose language comprehension, lexigram use, tool work, and development inside a language-rich social world made him one of the central figures in the ape-language research trajectory.",
    sections: [
      {
        id: "development",
        title: "Developmental Context",
        body:
          "Kanzi shifted the field from explicit training toward development. As an infant, he was exposed to lexigrams while researchers attempted to train his mother, Matata; his own keyboard use emerged without the same direct reinforcement structure used in earlier chimpanzee studies.",
        refs: ["r5", "r8"],
        links: [{ label: "Bonobos", href: "/research/bonobos" }],
      },
      {
        id: "comprehension",
        title: "Language Comprehension",
        body:
          "Kanzi's strongest evidence involved receptive language. The 1993 monograph compared his comprehension of spoken English with that of a young child, emphasizing novel requests, sentence structures, and understanding that was not simply trained keyboard production.",
        refs: ["r6"],
      },
      {
        id: "observational",
        title: "Observational Learning",
        body:
          "Later work on observational word learning treats Kanzi as part of a larger bonobo pattern: new words and referents can be mapped through social exposure, realistic settings, and limited direct instruction.",
        refs: ["r8"],
      },
      {
        id: "tools",
        title: "Tools and Culture",
        body:
          "Kanzi's stone-tool work connected language research to material culture. The tool papers and later culture papers use Kanzi to argue that communication, imitation, technical skill, and cultural practice cannot be cleanly separated.",
        refs: ["r7", "r10"],
      },
      {
        id: "trajectory",
        title: "Place in the Trajectory",
        body:
          "Kanzi is the figure through whom the archive becomes developmental and cultural. He shows why restoring the research trajectory means restoring the symbolic, social, and material conditions in which such capacities can be expressed.",
        refs: ["r5", "r6", "r10"],
      },
    ],
    references: ["r5", "r6", "r7", "r8", "r10"],
  },
  bonobos: {
    title: "Bonobos",
    contents: [
      ["overview", "Overview"],
      ["development", "Development"],
      ["comprehension", "Comprehension"],
      ["culture", "Culture"],
      ["ethics", "Ethical Stakes"],
      ["references", "References"],
    ],
    lead:
      "Bonobos are central to the later ape-language research trajectory because the work with Kanzi and related bonobos shifted the field toward development, culture, comprehension, and symbolic environments.",
    sections: [
      {
        id: "development",
        title: "Development",
        body:
          "The bonobo studies emphasize how language-like capacities emerge through social life, observation, daily routines, and access to a symbolic environment. This contrasts with narrow accounts of training as the sole source of symbol use.",
        refs: ["r5", "r8", "r10"],
      },
      {
        id: "comprehension",
        title: "Comprehension",
        body:
          "Bonobo language work places heavy weight on understanding, especially spoken English and novel requests. This makes comprehension, not just output, a core measure of the research trajectory.",
        refs: ["r6"],
      },
      {
        id: "culture",
        title: "Culture",
        body:
          "The culture papers argue that bonobo cognition is shaped by the environment in which it develops. Relationships, artifacts, gestures, lexigrams, speech, and shared routines are not background details; they are part of the cognitive ecology.",
        refs: ["r10"],
      },
      {
        id: "ethics",
        title: "Ethical Stakes",
        body:
          "The later Pan survival work frames bonobo and chimpanzee culture as something that can be destroyed or preserved. That makes research access, sanctuary conditions, and cultural continuity ethical questions as well as scientific ones.",
        refs: ["r9"],
      },
    ],
    references: ["r5", "r6", "r8", "r9", "r10"],
  },
  pan: {
    title: "Pan",
    contents: [
      ["overview", "Overview"],
      ["genus", "Genus"],
      ["language", "Language and Culture"],
      ["tools", "Tools and Memory"],
      ["ethics", "Ethical Horizon"],
      ["references", "References"],
    ],
    lead:
      "Pan is the genus that includes chimpanzees and bonobos. In the research trajectory, Pan names the broader cross-species question of language, culture, cognition, and moral responsibility.",
    sections: [
      {
        id: "genus",
        title: "Genus",
        body:
          "The trajectory includes both chimpanzee and bonobo work: Lana, Sherman, and Austin in the chimpanzee studies, and Kanzi, Panbanisha, Nyota, and other bonobos in the later developmental and cultural research.",
        refs: ["r1", "r3", "r5", "r6"],
      },
      {
        id: "language",
        title: "Language and Culture",
        body:
          "Across the archive, Pan communication is studied through lexigrams, spoken comprehension, gesture, tool use, observation, and social learning. The question is not a single ability but a constellation of capacities expressed under particular cultural conditions.",
        refs: ["r3", "r6", "r10"],
      },
      {
        id: "tools",
        title: "Tools and Memory",
        body:
          "The later Pan survival paper links lexigram-mediated testimony, representational mark-making, tool traditions, and cultural memory. It treats Pan cognition as historically and socially consequential.",
        refs: ["r9"],
      },
      {
        id: "ethics",
        title: "Ethical Horizon",
        body:
          "The ethical claim follows from the research claim: if chimpanzees and bonobos live within culture, communication, and social meaning, then captivity, habitat destruction, and research exclusion can damage not only individuals but cultures.",
        refs: ["r9"],
      },
    ],
    references: ["r1", "r3", "r5", "r6", "r9", "r10"],
  },
};

export const wikiTopicSources = {
  r1: {
    href: "/pdf/1978-Chimpanzee-Language-Research.pdf",
    label: "Rumbaugh & Savage-Rumbaugh, 1978",
    title: "Chimpanzee language research: Status and potential",
  },
  r2: {
    label: "Bettoni, 2007",
    title: "The Yerkish Language: From Operational Methodology to Chimpanzee Communication",
  },
  r3: {
    href: "/pdf/1978-Symbolic-Communication-Chimps.pdf",
    label: "Savage-Rumbaugh, Rumbaugh & Boysen, 1978",
    title: "Symbolic communication between two chimpanzees",
  },
  r4: {
    href: "/pdf/1980-Reference-Linguistic-Essential.pdf",
    label: "Savage-Rumbaugh et al., 1980",
    title: "Reference: The Linguistic Essential",
  },
  r5: {
    href: "/pdf/1986-Spontaneous-Symbol-Acquisition.pdf",
    label: "Savage-Rumbaugh et al., 1986",
    title: "Spontaneous symbol acquisition and communicative use by pygmy chimpanzees",
  },
  r6: {
    href: "/pdf/1993-Language-Comprehension-Ape.pdf",
    label: "Savage-Rumbaugh et al., 1993",
    title: "Language comprehension in ape and child",
  },
  r7: {
    href: "/pdf/1993-Pan-Tool-Maker.pdf",
    label: "Schick et al., 1993",
    title: "Continuing investigations into the stone tool-making and tool-using capabilities of a bonobo",
  },
  r8: {
    href: "/pdf/2000-Observational-Word-Learning-Bonobos.pdf",
    label: "Lyn, 2000",
    title: "Observational word learning in bonobos",
  },
  r9: {
    href: "/pdf/2019-Pan Survival.pdf",
    label: "Roffman et al., 2019",
    title: "Pan survival in the 21st century",
  },
  r10: {
    href: "/pdf/2005-Culture-Prefigures-Cognition.pdf",
    label: "Savage-Rumbaugh et al., 2005",
    title: "Culture prefigures cognition in Pan/Homo bonobos",
  },
};
